/**
 * arbi register -e email -p password --verification-code CODE   — fully scriptable
 * arbi register                                                  — interactive mode
 * arbi register --non-interactive [opts]                         — CI mode (SUPPORT_API_KEY)
 *
 * When all required flags are provided, no prompts are shown.
 * When some are missing, only the missing values are prompted.
 */

import { Command } from 'commander'
import 'fake-indexeddb/auto'
import { createArbiClient } from '@arbidocs/client'
import {
  performPasswordLogin,
  formatWorkspaceChoices,
  generateNewWorkspaceKey,
  workspaces as workspaceOps,
  projects as projectOps,
} from '@arbidocs/sdk'
import { success, error, ref } from '../output.js'
import { store, requireConfig, updateConfig } from '../config.js'
import { promptInput, promptPassword, promptConfirm, promptSelect } from '../prompts.js'
import { runAction, formatCliError } from '../helpers.js'
import { updateCompletionCache } from '../completion-cache.js'
import { withRetry, isRetryableStatus } from '../utils/central-retry.js'

const CENTRAL_API_URL = 'https://central.arbi.work'

// ── CI verification code fetch ───────────────────────────────────────────────

/** Raised when the central verify-ci endpoint returns a known error we can hint at. */
export class RegisterHintError extends Error {
  constructor(
    message: string,
    public readonly kind: 'agent-email-required' | 'bad-api-key' | 'already-registered'
  ) {
    super(message)
    this.name = 'RegisterHintError'
  }
}

/**
 * SUPPORT_API_KEY deployment keys only accept agent-*@<deploymentDomain> emails.
 * This heuristic lets us warn the caller before we even round-trip to central.
 */
export function looksLikeAgentEmail(email: string): boolean {
  const local = email.split('@')[0] ?? ''
  return local.toLowerCase().startsWith('agent-')
}

async function getVerificationCode(
  email: string,
  apiKey: string,
  deploymentDomain: string
): Promise<string> {
  const params = new URLSearchParams({ email })
  const url = `${CENTRAL_API_URL}/license-management/verify-ci?${params.toString()}`
  // Central IP-rate-limits CI bursts (429). Retry transient failures with backoff.
  const res = await withRetry(
    () => fetch(url, { method: 'GET', headers: { 'x-api-key': apiKey } }),
    { shouldRetry: (result, err) => err != null || isRetryableStatus(result?.status) }
  )

  if (!res.ok) {
    const body = await res.text().catch(() => '')
    // Translate common, confusing central-API errors into actionable messages.
    if (res.status === 403 && /agent-\*/i.test(body)) {
      throw new RegisterHintError(
        `This SUPPORT_API_KEY is a deployment key that can only register agent-*@${deploymentDomain} emails. ` +
          `Prefix your email with "agent-" (e.g. agent-<id>@${deploymentDomain}) or use a superuser key.`,
        'agent-email-required'
      )
    }
    if (res.status === 403 && /Invalid API Key/i.test(body)) {
      throw new RegisterHintError(
        'SUPPORT_API_KEY was rejected by central. Check the key value.',
        'bad-api-key'
      )
    }
    if (res.status === 404 && /No verification record/i.test(body)) {
      throw new RegisterHintError(
        `No pending verification for ${email}. The email may already be registered — ` +
          `try: arbi login --email ${email}`,
        'already-registered'
      )
    }
    throw new Error(`Failed to get verification code: ${res.status} ${body}`)
  }

  const data = (await res.json()) as { verification_words?: string[]; verification_code?: string }
  const words = data?.verification_words ?? data?.verification_code ?? null
  if (!words) throw new Error('No verification code in response')

  return Array.isArray(words) ? words.join(' ') : String(words)
}

// ── Command ──────────────────────────────────────────────────────────────────

interface RegisterOpts {
  nonInteractive?: boolean
  email?: string
  password?: string
  verificationCode?: string
  firstName?: string
  lastName?: string
  json?: boolean
  yes?: boolean
}

export function registerRegisterCommand(program: Command): void {
  program
    .command('register')
    .description('Register a new ARBI account')
    .option('--non-interactive', 'CI/automation mode (requires SUPPORT_API_KEY env var)')
    .option('-e, --email <email>', 'Email address (or ARBI_EMAIL env var)')
    .option('-p, --password <password>', 'Password (or ARBI_PASSWORD env var)')
    .option('-c, --verification-code <code>', 'Verification code (skip prompt)')
    .option('--first-name <name>', 'First name')
    .option('--last-name <name>', 'Last name')
    .option('-y, --yes', 'Skip the post-register "Log in now?" confirmation')
    .option('--json', 'Emit a single JSON object on success (scripting)')
    .action((opts: RegisterOpts) =>
      runAction(async () => {
        const config = requireConfig()

        if (opts.nonInteractive) {
          await nonInteractiveRegister(config, opts)
        } else {
          await smartRegister(config, opts)
        }
      })()
    )
}

// ── Smart register (flags > env > prompt) ────────────────────────────────────

async function smartRegister(
  config: { baseUrl: string; deploymentDomain: string },
  opts: RegisterOpts
) {
  // 1. Resolve email: flag > env > prompt
  let email = opts.email || process.env.ARBI_EMAIL || (await promptInput('Email'))

  // In interactive mode, auto-append domain only when --email flag was provided without @
  // (users typing at the prompt are expected to enter their full email)
  if ((opts.email || process.env.ARBI_EMAIL) && !email.includes('@')) {
    email = `${email}@${config.deploymentDomain}`
    if (!opts.json) console.log(`Using email: ${email}`)
  }

  const arbi = createArbiClient({
    baseUrl: config.baseUrl,
    deploymentDomain: config.deploymentDomain,
    credentials: 'omit',
  })
  await arbi.crypto.initSodium()

  // 2. Resolve verification code: flag > prompt
  let verificationCode: string
  if (opts.verificationCode) {
    verificationCode = opts.verificationCode
  } else {
    const codeMethod = await promptSelect('Verification method', [
      { name: 'I have an invitation code', value: 'code' },
      { name: 'Send me a verification email', value: 'email' },
    ])

    if (codeMethod === 'code') {
      verificationCode = await promptInput('Invitation code')
    } else {
      console.log('Sending verification email...')
      const verifyResponse = await withRetry(
        () => arbi.fetch.POST('/v1/user/verify-email', { body: { email } }),
        {
          shouldRetry: (result, err) =>
            err != null || (result?.error != null && isRetryableStatus(result.response?.status)),
        }
      )
      if (verifyResponse.error) {
        error(`Failed to send verification email: ${JSON.stringify(verifyResponse.error)}`)
        process.exit(1)
      }
      success('Verification email sent. Check your inbox.')
      verificationCode = await promptInput('Verification code')
    }
  }

  // 3. Resolve password: flag > env > prompt
  //    When provided via flag/env, skip confirmation prompt.
  let pw: string
  const flagOrEnvPassword = opts.password || process.env.ARBI_PASSWORD
  if (flagOrEnvPassword) {
    pw = flagOrEnvPassword
  } else {
    pw = await promptPassword('Password')
    const confirmPw = await promptPassword('Confirm password')
    if (pw !== confirmPw) {
      error('Passwords do not match.')
      process.exit(1)
    }
  }

  // 4. Resolve name: flag > prompt (skip prompts if all core flags provided)
  const hasAllCoreFlags =
    !!(opts.email || process.env.ARBI_EMAIL) &&
    !!(opts.password || process.env.ARBI_PASSWORD) &&
    !!opts.verificationCode
  const firstName =
    opts.firstName ||
    (hasAllCoreFlags ? 'User' : (await promptInput('First name', false)) || 'User')
  const lastName =
    opts.lastName || (hasAllCoreFlags ? '' : (await promptInput('Last name', false)) || '')

  // 5. Register
  try {
    await arbi.auth.register({
      email,
      password: pw,
      verificationCode,
      firstName,
      lastName,
    })
    if (!opts.json) success(`\nRegistered successfully as ${email}`)
  } catch (err) {
    if (opts.json) {
      console.log(JSON.stringify({ ok: false, stage: 'register', error: formatCliError(err) }))
    } else {
      error(`Registration failed: ${formatCliError(err)}`)
    }
    process.exit(1)
  }

  // 6. If all values were flags, auto-login. Otherwise offer choice (unless --yes).
  const allFlagsProvided =
    !!(opts.email || process.env.ARBI_EMAIL) &&
    !!(opts.password || process.env.ARBI_PASSWORD) &&
    !!opts.verificationCode
  const doLogin = allFlagsProvided || opts.yes || (await promptConfirm('Log in now?'))

  if (doLogin) {
    await loginAfterRegister(config, email, pw, { json: opts.json })
  } else if (opts.json) {
    console.log(JSON.stringify({ ok: true, email, server: config.baseUrl, logged_in: false }))
  }
}

// ── Non-interactive mode (CI with SUPPORT_API_KEY) ───────────────────────────

async function nonInteractiveRegister(
  config: { baseUrl: string; deploymentDomain: string },
  opts: RegisterOpts
) {
  let email = opts.email || process.env.ARBI_EMAIL
  const password = opts.password || process.env.ARBI_PASSWORD
  const supportApiKey = process.env.SUPPORT_API_KEY

  if (!email) {
    // In `--json` mode, return a structured error so an agent can branch
    // (`jq -r .error`) instead of having to grep stderr.
    if (opts.json) {
      console.log(
        JSON.stringify({
          ok: false,
          stage: 'preflight',
          error: 'Email required',
          hint: 'Use --email <email> or set ARBI_EMAIL',
        })
      )
      process.exit(1)
    }
    error('Email required. Use --email <email> or set ARBI_EMAIL')
    process.exit(1)
  }

  // Auto-append deployment domain for bare usernames (bot/test accounts)
  if (!email.includes('@')) {
    email = `${email}@${config.deploymentDomain}`
    if (!opts.json) console.log(`Using email: ${email}`)
  }

  if (!password) {
    if (opts.json) {
      console.log(
        JSON.stringify({
          ok: false,
          stage: 'preflight',
          error: 'Password required',
          hint: 'Use --password <password> or set ARBI_PASSWORD',
        })
      )
      process.exit(1)
    }
    error('Password required. Use --password <password> or set ARBI_PASSWORD')
    process.exit(1)
  }

  // Preflight: if using SUPPORT_API_KEY but the email doesn't start with "agent-",
  // warn client-side BEFORE the round-trip to central. Deployment-scoped keys
  // reject non-agent emails (the common case on box-*.arbibox.com). Superuser
  // keys accept any email; we can't distinguish up front, so we just warn.
  if (supportApiKey && !opts.verificationCode && !looksLikeAgentEmail(email)) {
    const hint =
      `Email "${email}" does not start with "agent-". ` +
      `SUPPORT_API_KEY deployment keys only accept agent-*@${config.deploymentDomain} emails. ` +
      `If your key is a superuser key this may still succeed — proceeding...`
    if (!opts.json) {
      console.error(`Warning: ${hint}`)
    }
  }

  const arbi = createArbiClient({
    baseUrl: config.baseUrl,
    deploymentDomain: config.deploymentDomain,
    credentials: 'omit',
  })
  await arbi.crypto.initSodium()

  // Resolve verification code: flag > SUPPORT_API_KEY CI endpoint
  let verificationCode: string
  if (opts.verificationCode) {
    verificationCode = opts.verificationCode
  } else {
    if (!supportApiKey) {
      if (opts.json) {
        console.log(
          JSON.stringify({
            ok: false,
            stage: 'preflight',
            error: 'Verification code required',
            hint: 'Use --verification-code <code> or set SUPPORT_API_KEY for CI mode',
          })
        )
        process.exit(1)
      }
      error(
        'Verification code required. Use --verification-code <code> or set SUPPORT_API_KEY for CI mode'
      )
      process.exit(1)
    }

    // Request verification email
    const verifyResponse = await withRetry(
      () => arbi.fetch.POST('/v1/user/verify-email', { body: { email } }),
      {
        shouldRetry: (result, err) =>
          err != null || (result?.error != null && isRetryableStatus(result.response?.status)),
      }
    )
    if (verifyResponse.error) {
      if (opts.json) {
        console.log(
          JSON.stringify({
            ok: false,
            stage: 'verify-email',
            error: JSON.stringify(verifyResponse.error),
          })
        )
      } else {
        error(`verify-email failed: ${JSON.stringify(verifyResponse.error)}`)
      }
      process.exit(1)
    }

    // Fetch code from CI endpoint — surfaces friendly messages for common errors.
    try {
      verificationCode = await getVerificationCode(email, supportApiKey, config.deploymentDomain)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      if (opts.json) {
        console.log(
          JSON.stringify({
            ok: false,
            stage: 'verify-ci',
            error: msg,
            kind: err instanceof RegisterHintError ? err.kind : undefined,
          })
        )
      } else {
        error(`Error: ${msg}`)
      }
      process.exit(1)
    }
  }

  // Register
  try {
    await arbi.auth.register({
      email,
      password,
      verificationCode,
      firstName: opts.firstName ?? 'Test',
      lastName: opts.lastName ?? 'User',
    })
    if (!opts.json) success(`Registered: ${email}`)
  } catch (err) {
    if (opts.json) {
      console.log(JSON.stringify({ ok: false, stage: 'register', error: formatCliError(err) }))
    } else {
      error(`Registration failed: ${formatCliError(err)}`)
    }
    process.exit(1)
  }

  // Auto-login and create workspace in non-interactive mode
  await loginAfterRegister(config, email, password, { json: opts.json })
}

// ── Post-registration login ──────────────────────────────────────────────────

async function loginAfterRegister(
  config: { baseUrl: string; deploymentDomain: string },
  email: string,
  password: string,
  { json = false }: { json?: boolean } = {}
) {
  try {
    const { arbi, loginResult } = await performPasswordLogin(config, email, password, store)
    if (!json) success(`Logged in as ${email}`)

    const { data: workspaces } = await arbi.fetch.GET('/v1/user/workspaces')
    const wsList = workspaces || []
    updateCompletionCache(wsList)

    const memberWorkspaces = wsList.filter((ws) => ws.users?.some((u) => u.user.email === email))

    let selectedWorkspace: { external_id: string; name: string } | undefined

    if (memberWorkspaces.length === 0) {
      if (!json) console.log('Creating your first workspace...')
      const userProjects = await projectOps.listProjects(arbi)
      const defaultProjectExtId = userProjects[0]?.external_id
      if (!defaultProjectExtId) throw new Error('No projects found for user')
      const encryptedKey = await generateNewWorkspaceKey(arbi, loginResult.serverSessionKey)
      const ws = await workspaceOps.createWorkspace(
        arbi,
        'My First Workspace',
        encryptedKey,
        defaultProjectExtId
      )
      updateConfig({ selectedWorkspaceId: ws.external_id })
      selectedWorkspace = { external_id: ws.external_id, name: ws.name }
      if (!json) success(`Workspace: ${ws.name} (${ref(ws.external_id)})`)
    } else if (memberWorkspaces.length === 1) {
      updateConfig({ selectedWorkspaceId: memberWorkspaces[0].external_id })
      selectedWorkspace = {
        external_id: memberWorkspaces[0].external_id,
        name: memberWorkspaces[0].name,
      }
      if (!json)
        success(`Workspace: ${memberWorkspaces[0].name} (${ref(memberWorkspaces[0].external_id)})`)
    } else if (json) {
      // JSON mode with multiple workspaces: don't prompt; caller can pick via
      // `arbi workspace select` or `arbi login --workspace <id>`.
    } else {
      const choices = formatWorkspaceChoices(memberWorkspaces)
      const selected = await promptSelect('Select workspace', choices)
      updateConfig({ selectedWorkspaceId: selected })
      const ws = memberWorkspaces.find((w) => w.external_id === selected)!
      selectedWorkspace = { external_id: selected, name: ws.name }
      success(`Workspace: ${ws.name} (${ref(selected)})`)
    }

    if (json) {
      console.log(
        JSON.stringify({
          ok: true,
          email,
          server: config.baseUrl,
          logged_in: true,
          workspace: selectedWorkspace ?? null,
          workspaces_available: memberWorkspaces.length,
        })
      )
    }
  } catch (err) {
    if (json) {
      console.log(
        JSON.stringify({ ok: false, stage: 'post-register-login', error: formatCliError(err) })
      )
    } else {
      error(`Login failed: ${formatCliError(err)}`)
      error('You can log in later with: arbi login')
    }
  }
}
